import ErrorBoundary from "components/ErrorBoundary";
import LayoutRegistered from "components/LayoutRegistered";
import LayoutUnregistered from "components/LayoutUnregistered";
import NotFound from "components/Pages/NotFound";
import RouterSuspense from "components/RouterSuspense";
import { fullStatChannels } from "constants/fullStatsChannels";
import usePermissions from "helpers/usePermissions";
import React from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";

const CompanyPackages = React.lazy(() =>
  import("components/Pages/CompanyPackages")
);
const StatisticsLight = React.lazy(() =>
  import("components/Pages/Statistics/StatisticsLight")
);
const Followers = React.lazy(() =>
  import("components/Pages/Statistics/Followers")
);

const Economy = React.lazy(() => import("components/Pages/Economy"));
const Receipts = React.lazy(() => import("components/Pages/Economy/Receipts"));

const Library = React.lazy(() => import("components/Pages/Library"));
const Coaches = React.lazy(() => import("components/Pages/Coaches"));
const Exercises = React.lazy(() => import("components/Pages/Exercises"));
const Users = React.lazy(() => import("components/Pages/Statistics/Users"));
const Subscribers = React.lazy(() =>
  import("components/Pages/Statistics/Subscribers")
);
const Logs = React.lazy(() => import("components/Pages/Statistics/Logs"));
const Items = React.lazy(() => import("components/Pages/Statistics/Items"));
const Policy = React.lazy(() => import("components/Pages/Policy"));
const Campaign = React.lazy(() => import("components/Pages/Campaigns"));
const CampaignEditor = React.lazy(() =>
  import("components/Pages/Campaigns/Editor")
);
const CampaignSingle = React.lazy(() =>
  import("components/Pages/Campaigns/Single")
);
const TemplatePreview = React.lazy(() =>
  import("components/Pages/Campaigns/Single/TemplatePreview")
);
const Coaching = React.lazy(() => import("components/Pages/Coaching"));
const Dashboard = React.lazy(() => import("components/Pages/Dashboard"));
const Statistics = React.lazy(() => import("components/Pages/Statistics"));
const Notes = React.lazy(() => import("components/Pages/Coaching/Notes"));
const Invitations = React.lazy(() =>
  import("components/Pages/Coaching/Invitations")
);
const SocialGroup = React.lazy(() =>
  import("components/Pages/Coaching/SocialGroup")
);
const Product = React.lazy(() => import("components/Pages/Product"));
const Personal = React.lazy(() => import("components/Pages/Product/Personal"));
const Auth = React.lazy(() => import("components/Pages/Auth"));
const Guides = React.lazy(() => import("components/Pages/Guides"));
const Messages = React.lazy(() => import("components/Pages/Messages"));
const Profile = React.lazy(() => import("components/Pages/Profile"));
const Notifications = React.lazy(() =>
  import("components/Pages/Notifications")
);
const Single = React.lazy(() => import("components/Pages/Coaches/Single"));
const Customers = React.lazy(() =>
  import("components/Pages/Coaching/Customers")
);
const Stream = React.lazy(() => import("components/Pages/Coaching/Stream"));
const SingleCustomer = React.lazy(() =>
  import("components/Pages/Coaching/SingleCustomer")
);
const SingleSocialGroup = React.lazy(() =>
  import("components/Pages/Coaching/SingleSocialGroup")
);

const Collections = React.lazy(() =>
  import("components/Pages/Store/Collections")
);
const StoreOverview = React.lazy(() =>
  import("components/Pages/Store/Overview")
);
const ChannelProducts = React.lazy(() =>
  import("components/Pages/Store/ChannelProducts")
);
const ContentAccess = React.lazy(() =>
  import("components/Pages/Store/ContentAccess")
);
const SingleProductPackage = React.lazy(() =>
  import("components/Pages/Store/ContentAccess/SingleProductPackage")
);
const SingleCat = React.lazy(() => import("components/Pages/Store/SingleCat"));

const Teamboosts = React.lazy(() => import("components/Pages/Teamboosts"));
const SingleTeamboost = React.lazy(() =>
  import("components/Pages/Teamboosts/SingleTeamboost")
);

function Pages() {
  const perm = usePermissions(); // Get channel permissions

  const selectedChannel = useSelector((state) => state.selectedChannel);
  const insightsMode = useSelector((state) => state.insightsMode, shallowEqual);
  const fullInsights = fullStatChannels.includes(selectedChannel?.id);

  return (
    <Routes>
      {perm.campaignPages !== "none" && (
        <Route element={<RouterSuspense />}>
          <Route path="/campaign/:id" element={<CampaignSingle />} />
          <Route path="/campaign/templates/:id" element={<TemplatePreview />} />
        </Route>
      )}

      {perm.campaignPages === "write" && (
        <Route path="/campaign/:id/edit" element={<RouterSuspense />}>
          <Route index element={<CampaignEditor />} />
          <Route path=":mode" element={<CampaignEditor />} />
        </Route>
      )}

      <Route element={<LayoutRegistered />}>
        <Route path="/" element={<Dashboard />} />

        {perm.finance !== "none" && (
          <Route path="/economy">
            <Route index element={<Economy />} />
            <Route path="receipts" element={<Receipts />} />
          </Route>
        )}

        <Route path="store/products">
          <Route index element={<Navigate to="program" />} />
          <Route path=":panel" element={<ChannelProducts />} />
        </Route>

        <Route path="store/content-access">
          <Route index element={<Navigate to="product-packages" />} />
          <Route path=":panel" element={<ContentAccess />} />
          <Route path="product-packages/:id">
            <Route index element={<Navigate to="products" />} />
            <Route path=":panel" element={<SingleProductPackage />} />
          </Route>
        </Route>

        {perm.shop !== "none" || perm.campaignPages !== "none" ? (
          <Route path="store">
            <Route index element={<StoreOverview />} />
            {perm.shop !== "none" && (
              <Route path={":panel"} element={<Collections />} />
            )}
            {perm.shop !== "none" && (
              <Route path={":panel/:id"} element={<SingleCat />} />
            )}
            {perm.campaignPages !== "none" && (
              <Route path="campaigns" element={<Campaign />} />
            )}
          </Route>
        ) : null}

        {perm.insights !== "none" && (
          <Route path="/statistics">
            <Route
              index
              element={
                insightsMode === "domain" ? <Statistics /> : <StatisticsLight />
              }
            />
            {fullInsights ? (
              <Route
                path="users"
                element={
                  <ErrorBoundary>
                    <Users />
                  </ErrorBoundary>
                }
              />
            ) : null}
            <Route
              path="followers"
              element={
                <ErrorBoundary>
                  <Followers />
                </ErrorBoundary>
              }
            />
            <Route
              path="subscribers"
              element={
                <ErrorBoundary>
                  <Subscribers />
                </ErrorBoundary>
              }
            />
            <Route
              path="logs"
              element={
                <ErrorBoundary>
                  <Logs />
                </ErrorBoundary>
              }
            />
            <Route
              path="items"
              element={
                <ErrorBoundary>
                  <Items />
                </ErrorBoundary>
              }
            />
          </Route>
        )}

        <Route path="companyPackages">
          <Route index element={<CompanyPackages />} />
        </Route>

        {perm.teamboosts !== "none" && (
          <Route path="teamboost">
            <Route index element={<Teamboosts />} />
            <Route path=":id/:panel" element={<SingleTeamboost />} />
          </Route>
        )}

        {perm.coaching !== "none" && (
          <Route path="library">
            <Route index element={<Navigate to="workouts" />} />
            <Route path={":panel"} element={<Library />} />
          </Route>
        )}

        {perm.exerciseLibrary !== "none" && (
          <Route path="exercises" element={<Exercises />} />
        )}

        {perm.coaches !== "none" && (
          <Route path="coaches">
            <Route index element={<Coaches />} />
            <Route path=":id" element={<Single />} />
          </Route>
        )}

        {perm.coaching !== "none" && (
          <Route path="coaching">
            <Route index element={<Coaching />} />
            <Route path="product" element={<Navigate to="/coaching" />} />
            <Route path="product/:id/:panel" element={<Product />} />
            <Route path="product/personal/:id/:panel" element={<Personal />} />
            <Route path="customers">
              <Route index element={<Customers />} />
              <Route path=":id/:panel" element={<SingleCustomer />} />
            </Route>
            <Route path="stream" element={<Stream />} />
            <Route path="notes" element={<Notes />} />
            <Route path="invitations" element={<Invitations mode="" />} />
            <Route path="social" element={<SocialGroup />} />
            <Route path="social/:id/:panel" element={<SingleSocialGroup />} />
            <Route path=":panel" element={<Coaching />} />
          </Route>
        )}

        <Route path={"/guides"} element={<Guides />} />
        <Route path={"/notifications"} element={<Notifications />} />
        <Route path={"/messages"} element={<Messages />}></Route>
        <Route path={"/messages/:id"} element={<Messages />} />

        <Route path="profile">
          <Route index element={<Profile />} />
          <Route path=":panel" element={<Profile />} />
        </Route>
      </Route>

      <Route element={<LayoutUnregistered />}>
        <Route path={"/login"} element={<Auth type="login" />} />
        <Route path="/reset" element={<Auth type="reset" />} />

        <Route path="/register" element={<Auth type="register" />}>
          <Route path=":stage" element={<Auth type="register" />}></Route>
        </Route>

        <Route path="/privacy-policy" element={<Policy />} />
      </Route>

      <Route element={<LayoutUnregistered notFound />}>
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default React.memo(Pages);
